import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { call, callableMessage } from "./callables";
import { CartItem, OrderModification, QuantityChange } from "../types/index";

/**
 * Calculate price difference for order modifications
 */
export const calculateModificationDifference = (
  originalItems: CartItem[],
  newItems: CartItem[],
  cancelledItemIds: string[]
): {
  difference: number;
  quantityChanges: QuantityChange[];
  cancelledItems: CartItem[];
  refundAmount: number;
  paymentRequired: boolean;
} => {
  const cancelledItems: CartItem[] = [];
  const quantityChanges: QuantityChange[] = [];
  let totalDifference = 0;

  // 1. Calculate cancelled items
  originalItems.forEach(originalItem => {
    if (cancelledItemIds.includes(originalItem.id)) {
      const itemTotal = originalItem.price * originalItem.quantity;
      totalDifference -= itemTotal;
      cancelledItems.push(originalItem);
    }
  });

  // 2. Calculate quantity changes
  originalItems.forEach(originalItem => {
    if (!cancelledItemIds.includes(originalItem.id)) {
      const newItem = newItems.find(item => item.id === originalItem.id);
      
      if (newItem && newItem.quantity !== originalItem.quantity) {
        const qtyDiff = newItem.quantity - originalItem.quantity;
        const priceDiff = qtyDiff * originalItem.price;
        
        totalDifference += priceDiff;
        
        quantityChanges.push({
          itemId: originalItem.id,
          itemName: originalItem.name,
          oldQuantity: originalItem.quantity,
          newQuantity: newItem.quantity,
          pricePerItem: originalItem.price,
          priceDifference: priceDiff
        });
      }
    }
  });

  return {
    difference: totalDifference,
    quantityChanges,
    cancelledItems,
    refundAmount: totalDifference < 0 ? Math.abs(totalDifference) : 0,
    paymentRequired: totalDifference > 0
  };
};

/**
 * Process order modification. Runs server-side (modifyOrder callable): prices come from the
 * stored order, wallet refunds/charges are applied with ledger records.
 */
export const modifyOrder = async (
  orderId: string,
  _userId: string,
  newItems: CartItem[],
  cancelledItemIds: string[],
  paymentMethod?: 'wallet' | 'upi' | 'food'
): Promise<{ success: boolean; modificationId?: string; message: string }> => {
  try {
    const res = await call<{ success: boolean; modificationId?: string; message: string }>("modifyOrder", {
      orderId,
      newItems: newItems.map((i) => ({ id: i.id, quantity: i.quantity })),
      cancelledItemIds,
      paymentMethod: paymentMethod || null,
    });
    return { success: !!res?.success, modificationId: res?.modificationId, message: res?.message || "Order updated" };
  } catch (error: any) {
    console.error("Error modifying order:", error);
    return { success: false, message: callableMessage(error, "Failed to modify order") };
  }
};

/**
 * Get modification history for an order
 */
export const getOrderModifications = async (orderId: string): Promise<OrderModification[]> => {
  try {
    const orderRef = doc(db, "orders", orderId);
    const orderSnap = await getDoc(orderRef);

    if (!orderSnap.exists() || !orderSnap.data().modificationHistory) {
      return [];
    }

    const modificationIds = orderSnap.data().modificationHistory as string[];
    const modifications: OrderModification[] = [];

    for (const modId of modificationIds) {
      const modRef = doc(db, "order_modifications", modId);
      const modSnap = await getDoc(modRef);
      
      if (modSnap.exists()) {
        modifications.push({
          id: modSnap.id,
          ...modSnap.data()
        } as OrderModification);
      }
    }

    return modifications;
  } catch (error) {
    console.error("Error fetching order modifications:", error);
    return [];
  }
};
